// src/features/variant_workflow/clinical_observation/ReportCategoryModal.tsx
import { Alert, Button, Input, Select } from 'antd';
import { Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import { VariantProbandReportData, VariantSample } from '../redux';
import { reportCategoryValidationSchema } from '../validation_schemas/report_category_validation_schema';
import { useReportCategoryModalLogic } from './hooks';

interface FormValues {
  report_category: string | undefined;
}

type ReportCategoryModalProps = {
  onClose: () => void;
  reportData: VariantProbandReportData;
  variantSamples: VariantSample[];
  selectedRecord: VariantSample;
  setSelectedRecord: React.Dispatch<
    React.SetStateAction<VariantSample | undefined>
  >;
  setReportData: React.Dispatch<
    React.SetStateAction<VariantProbandReportData | undefined>
  >;
};

const ReportCategoryModal = ({
  onClose,
  reportData,
  variantSamples,
  selectedRecord,
  setSelectedRecord,
  setReportData,
}: ReportCategoryModalProps) => {
  const { isReadOnly, canInteract } = useVariantAccess();
  const lock = useSelector(selectVariantLock);

  // All logic extracted to custom hook for separation of concerns
  const {
    isReportModalLoading,
    reportCategoryOptions,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleFormSubmit,
  } = useReportCategoryModalLogic({
    onClose,
    reportData,
    variantSamples,
    selectedRecord,
    setSelectedRecord,
    setReportData,
  });

  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isReportModalLoading && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Formik
        initialValues={initialValues as FormValues}
        onSubmit={handleFormSubmit}
        validationSchema={reportCategoryValidationSchema}
      >
        {({
          errors,
          values,
          handleSubmit,
          handleBlur,
          isValid,
          dirty,
          setFieldValue,
        }) => {
          const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

          return (
            <form onSubmit={handleSubmit}>
              <div className="c-form-field">
                <div className="c-form-field__label">Report panel:</div>
                <div className="c-form-field__wrapper">
                  <Input
                    name="report_panel"
                    type="text"
                    value={reportData.medicalinvestigation_name}
                    placeholder="Report panel"
                    disabled
                    className="h-proband-modal__input-field-disabled-highlight"
                  />
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Report category:</div>
                <div className="c-form-field__wrapper">
                  <Select
                    value={values.report_category}
                    onChange={(v) => setFieldValue('report_category', v)}
                    onBlur={handleBlur}
                    className="h-proband-modal__select-field"
                    options={reportCategoryOptions}
                    placeholder="Choose variant zygosity for proband"
                    disabled={!canInteract}
                    status={!canInteract ? 'error' : undefined}
                  />
                  <div className="c-form-field__error">
                    {errors.report_category
                      ? errors.report_category
                      : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="h-proband-modal__form-submit-btn"
                  disabled={isSaveDisabled}
                >
                  Save changes
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ReportCategoryModal;
